/**
 * The station-volumes chart (spec §4b): two stacked panes on one shared
 * station axis — cut/fill bars diverging around zero above, the cumulative
 * mass-haul curve below — with a hand-rolled brush, a walked-station cursor
 * and station-equation markers.
 *
 * Two panes and not a secondary y-axis: on a dual-scale axis the crossing
 * point of the two series is an artefact of the scales, and on a mass-haul
 * chart that crossing point looks exactly like a balance point. Two panes at
 * 2 : 1 read the same with no lie.
 *
 * The brush is hand-rolled on a transparent overlay (the
 * `ElevationProfilePanel.tsx:694-704` pattern) because `@visx/brush` is not a
 * dependency of this app and a brush is 60 lines.
 *
 * Pure layout / mark geometry lives in `@/lib/stationVolumes/chartLayout`.
 */
import { alignmentWalkAtom } from '@/atoms/alignment.atom';
import { stationVolumeHoverBinAtom, stationVolumeSelectionAtom } from '@/atoms/stationVolumes.atom';
import { SEVERITY } from '@/components/VerdictChip';
import { internalToDisplayRaw } from '@/core/alignment';
import { CUT_LEGEND_KEY, FILL_LEGEND_KEY } from '@/lib/cutFillConvention';
import { balancePoints, binCentreStation, binIndexForStation, groupBins } from '@/lib/stationVolumes/aggregate';
import { COVERAGE_BANDS } from '@/lib/stationVolumes/coverageBands';
import {
  barDomain,
  barWidthFor,
  CHART_MARGIN,
  chartLayout,
  crossfallDomain,
  crossfallPaths,
  EQUATION_LABEL_MIN_GAP_PX,
  equationMarkers,
  LABEL_MIN_GAP_PX,
  maxBarsFor,
  endAreaDomain,
  endAreaPaths,
  PANE_GAP_PX,
  thinLabels,
} from '@/lib/stationVolumes/chartLayout';
import {
  formatM2,
  formatM3Row,
  formatPct,
  formatSigned,
  m3Formatter,
  staOnly,
  staTag,
  STATION_VOLUME_DISPLAY_DECIMALS,
} from '@/lib/stationVolumes/format';
import type { CrossfallSide, StationVolumesAnalysisKind, StationVolumesJson } from '@/lib/stationVolumes/types';
import { AxisBottom } from '@visx/axis';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  approxLabelPx,
  axisTickFormatter,
  BarPane,
  CHART_CSS,
  ChartLegend,
  CrossfallPane,
  FLOATING_TOOLTIP_STYLE,
  EndAreaPane,
  HaulPane,
  HiddenDataTable,
  stationTickLabelProps,
  TICK_FONT,
  TooltipCard,
} from './StationVolumeChartParts';

export interface StationVolumeChartProps {
  json: StationVolumesJson;
  layerId: string;
  /** Measured width of the dock, not the window (drives super-binning). */
  width: number;
  /** True below DOCK_NARROW_PX — collapse to one pane + a mass-haul toggle. */
  compact: boolean;
  /**
   * Which analysis produced the run — copy only. A deviation run compares a
   * point cloud with a design model, not "two surfaces", and the low-coverage
   * explanation is the one sentence in this view that names them.
   */
  kind?: StationVolumesAnalysisKind;
}

/** The dock body's horizontal padding (`px-4`), which the chart sits inside. */
const BODY_PADDING_PX = 32;
const MIN_PLOT_W = 240;
/**
 * Absolute floor for the plot, NOT a target: it only keeps the SVG off zero
 * while the dock is dragged to its 180 px minimum. The MEASURED body height
 * always wins above it — a floor taller than the body is what pushed the
 * legend row out under the dock footer at the default 280 px height, and the
 * two panes share whatever is left (`chartLayout` splits 2:1, so the mass-haul
 * pane is the one that gives first).
 */
const MIN_PLOT_H = 40;
/** Used until the ResizeObserver reports — the dock's default body height. */
const DEFAULT_PLOT_H = 200;
/**
 * How near a brush edge a pointer must land to grab that handle. ±12 px is a
 * 24 px target, the A11y floor (spec §5 point 5); the visible grip is narrower
 * because a 24 px-wide grip would hide the bins under it.
 */
/** The per-station coverage strip under the bars (`coverageBands.ts`). */
const COVERAGE_STRIP_PX = 6;
const COVERAGE_STRIP_GAP_PX = 3;
const HANDLE_GRAB_PX = 12;
/** Visible brush grip: ≥ 24 px tall so it is findable, 8 px wide so it is not in the way. */
const HANDLE_GRIP_H = 28;
const HANDLE_GRIP_W = 8;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export const StationVolumeChart = ({
  json,
  layerId,
  width,
  compact,
  kind = 'surfaceDifference',
}: StationVolumeChartProps) => {
  const { t } = useTranslation('project');
  const [selection, setSelection] = useAtom(stationVolumeSelectionAtom);
  // Read AND write: the crosshair follows the table's hover too, so the two
  // views are one instrument rather than two.
  const hoverBin = useAtomValue(stationVolumeHoverBinAtom);
  const setHover = useSetAtom(stationVolumeHoverBinAtom);
  const [walk, setWalk] = useAtom(alignmentWalkAtom);
  const [massHaul, setMassHaul] = useState(false);
  /**
   * What the LOWER pane shows. Its own pane and its own scale, never a second
   * y-axis on the bar pane: m³ and m² share no datum, and on a dual-scale axis
   * the crossing of the two series is an artefact of the scales.
   *
   * Offered only when the run carries true station-line areas — a run without
   * them has nothing to plot at a chainage.
   */
  const [lowerPane, setLowerPane] = useState<'haul' | 'endArea' | 'crossfall'>('haul');
  /** Drives the overlay's cursor: the brush handles have no hit area of their
   *  own (the overlay does all the hit-testing), so the affordance has to be
   *  the pointer itself. */
  const [cursor, setCursor] = useState<'crosshair' | 'ew-resize' | 'grabbing'>('crosshair');
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  const plotRef = useRef<HTMLDivElement | null>(null);
  // Only ever read inside event handlers — the React Compiler forbids reading
  // a ref during render.
  const lastTipRef = useRef<number | null>(null);
  const draggingRef = useRef(false);
  /** Owns the window listeners of an in-flight drag, so unmounting mid-drag
   *  (the dock closing under the pointer) cannot leak them. */
  const dragAbortRef = useRef<AbortController | null>(null);
  const { containerRef, TooltipInPortal } = useTooltipInPortal({ scroll: true, detectBounds: true, zIndex: 9999 });
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<{
    group: number;
  }>();

  // Measure the plot box itself: the dock passes its own width, but the body
  // is padded and the body's height is whatever the resize grip left over.
  useEffect(() => {
    const el = plotRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r || typeof r.width !== 'number' || typeof r.height !== 'number') return;
      if (r.width <= 0 || r.height <= 0) return;
      setBox((prev) => (prev && prev.w === r.width && prev.h === r.height ? prev : { w: r.width, h: r.height }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A drag in flight when this unmounts (the dock closed under the pointer)
  // would otherwise leave `pointermove` on the window for the rest of the
  // session, writing into a dead component's atoms.
  useEffect(() => () => dragAbortRef.current?.abort(), []);

  // The hover atom is a bare bin index with no layer key, and a dock closing
  // under a resting pointer fires no `pointerleave` — clear it on the way out
  // so the next dock does not inherit a highlight (clearance review #7, the
  // same hazard here).
  useEffect(() => () => setHover(null), [setHover]);

  const plotWidth = Math.max(MIN_PLOT_W, box?.w ?? width - BODY_PADDING_PX);
  const plotHeight = Math.max(MIN_PLOT_H, box?.h ?? DEFAULT_PLOT_H);
  const baseLayout = useMemo(() => chartLayout(plotWidth, plotHeight, compact), [plotWidth, plotHeight, compact]);

  // ------------------------------------------------------------ y gutter
  /**
   * The left gutter is sized to the widest value-axis label it has to hold
   * (audit §A9: the cumulative axis's "−12,000" was clipped at the dock's
   * edge in a fixed 56 px). The bar domain depends on the grouping, which
   * depends on the inner width, which depends on the gutter — so the labels
   * are taken from a first pass at the default gutter; a regrouping only
   * ever moves a domain by one bin's worth, never by a digit.
   */
  const gutter = useMemo(() => {
    const pre = groupBins(json.bins, maxBarsFor(baseLayout.innerWidth)).bins;
    const barTicks = scaleLinear<number>({ domain: barDomain(pre, Math.max(1, baseLayout.barHeight)) }).ticks(4);
    let min = 0;
    let max = 0;
    for (const b of json.bins) {
      min = Math.min(min, b.cumulativeNetM3);
      max = Math.max(max, b.cumulativeNetM3);
    }
    const haulTicks = scaleLinear<number>({ domain: max - min < 1e-9 ? [-1, 1] : [min, max] }).ticks(3);
    const labels = [...barTicks.map(axisTickFormatter(barTicks)), ...haulTicks.map(axisTickFormatter(haulTicks))];
    // Tick (6 px) + the 0.25 em gap + 4 px of air from the dock edge.
    const widest = labels.reduce((w, l) => Math.max(w, approxLabelPx(l)), 0);
    return Math.max(CHART_MARGIN.left, widest + 13);
  }, [json.bins, baseLayout.innerWidth, baseLayout.barHeight]);
  const margin = useMemo(() => ({ ...CHART_MARGIN, left: gutter }), [gutter]);
  const layout = useMemo(
    () => ({ ...baseLayout, margin, innerWidth: Math.max(1, plotWidth - margin.left - margin.right) }),
    [baseLayout, margin, plotWidth],
  );
  const { innerWidth } = layout;

  // Above ~1 bar per 3 px, aggregate by SUMMATION. Exact for volumes, so
  // nothing is resampled; the table, brush and CSV stay at full resolution.
  const grouped = useMemo(() => groupBins(json.bins, maxBarsFor(innerWidth)), [json.bins, innerWidth]);
  const bars = grouped.bins;
  const groupSize = grouped.groupSize;
  const nBins = json.bins.length;

  // ------------------------------------------------------------ pane boxes
  const stationLines = json.stations ?? null;
  const canShowEndArea = !!stationLines && stationLines.length > 0;
  const endAreaOn = canShowEndArea && lowerPane === 'endArea';
  /**
   * Offered only when the run carried a check AND the true station lines it
   * hangs off: a bin-mean run has no crossfall at all. `params.crossfall` is
   * the bands and tolerance the run was made with; a run made before the
   * field existed carries only the summary, so either is enough to offer it.
   */
  const crossfallTol = json.params.crossfall?.tolerancePct ?? json.summary.crossfall?.tolerancePct ?? 0;
  const canShowCrossfall =
    !!stationLines &&
    stationLines.length > 0 &&
    (json.params.crossfall != null || json.summary.crossfall != null) &&
    // …and the block actually reached the station lines. A run whose params
    // recorded a check but whose lines carry nothing would offer a radio that
    // switches to an empty pane.
    stationLines.some((s) => s.crossfall != null);
  const crossfallOn = canShowCrossfall && lowerPane === 'crossfall';
  const compactHaul = compact && massHaul;
  const showBars = !compactHaul;
  const showLower = !compact || compactHaul;
  const showHaul = showLower && !endAreaOn && !crossfallOn;
  const showEndArea = showLower && endAreaOn;
  const showCrossfall = showLower && crossfallOn;
  const barPaneH = compactHaul ? 0 : compact ? layout.barHeight : Math.max(1, layout.barHeight - PANE_GAP_PX);
  // The coverage strip takes the bottom of the bar pane, under the cut bars.
  const barPlotH = Math.max(1, barPaneH - COVERAGE_STRIP_PX - COVERAGE_STRIP_GAP_PX);
  const haulPaneTop = compactHaul ? 0 : layout.barHeight;
  const haulPaneH = compactHaul ? layout.barHeight : layout.haulHeight;
  const axisY = layout.barHeight + layout.haulHeight;

  // ------------------------------------------------------------ scales
  const lo = json.params.stationFromInternalM;
  const hi = json.params.stationToInternalM;
  const xScale = useMemo(
    () => scaleLinear<number>({ domain: [lo, hi > lo ? hi : lo + 1], range: [0, innerWidth] }),
    [lo, hi, innerWidth],
  );
  /**
   * NOT symmetric about zero, and not an oversight — see `barDomain`. The
   * scale is linear, so equal cut and fill magnitudes still draw equal
   * heights; the domain just stops where the data does, plus the headroom
   * the extreme bar's own label needs.
   */
  const barDom = useMemo(() => barDomain(bars, barPlotH), [bars, barPlotH]);
  const yBar = useMemo(
    () => scaleLinear<number>({ domain: barDom, range: [barPlotH, 0] }),
    [barDom, barPlotH],
  );
  const cumDomain = useMemo<[number, number]>(() => {
    let min = 0;
    let max = 0;
    for (const b of json.bins) {
      if (b.cumulativeNetM3 < min) min = b.cumulativeNetM3;
      if (b.cumulativeNetM3 > max) max = b.cumulativeNetM3;
    }
    if (max - min < 1e-9) return [-1, 1];
    const pad = (max - min) * 0.08;
    return [min - pad, max + pad];
  }, [json.bins]);
  const yHaul = useMemo(
    () => scaleLinear<number>({ domain: cumDomain, range: [Math.max(1, haulPaneH), 0] }),
    [cumDomain, haulPaneH],
  );

  const endAreaDom = useMemo(() => endAreaDomain(stationLines ?? []), [stationLines]);
  const yEndArea = useMemo(
    () => scaleLinear<number>({ domain: endAreaDom, range: [Math.max(1, haulPaneH), 0] }),
    [endAreaDom, haulPaneH],
  );
  const endAreaSeries = useMemo(
    () => (stationLines ? endAreaPaths(stationLines, (v) => xScale(v), (v) => yEndArea(v)) : { cut: '', fill: '' }),
    [stationLines, xScale, yEndArea],
  );

  const crossfallDom = useMemo(() => crossfallDomain(stationLines ?? [], crossfallTol), [stationLines, crossfallTol]);
  const yCrossfall = useMemo(
    () => scaleLinear<number>({ domain: crossfallDom, range: [Math.max(1, haulPaneH), 0] }),
    [crossfallDom, haulPaneH],
  );
  const crossfallSeries = useMemo(
    () =>
      crossfallPaths(
        stationLines ?? [],
        (v) => xScale(v),
        (v) => yCrossfall(v),
        crossfallTol,
      ),
    [stationLines, xScale, yCrossfall, crossfallTol],
  );

  // ------------------------------------------------------------ derived marks
  /**
   * The mass-haul curve. `cumulativeNetM3` is the running sum AT EACH BIN'S
   * END station (the value before bin 0 is 0 at bin 0's start), so the curve
   * is drawn end-to-end with a leading zero — plotting it at bin centres
   * would put the balance markers, which interpolate between end stations,
   * off the line they belong to.
   *
   * Points that land in a pixel column already used are dropped: a 20 000-bin
   * corridor is otherwise 20 000 path commands for a 900 px line.
   */
  const haulPath = useMemo(() => {
    if (json.bins.length === 0) return '';
    const pts: string[] = [];
    let lastPx = Number.NaN;
    const push = (station: number, cum: number, force: boolean) => {
      const px = xScale(station);
      if (!force && Math.round(px) === Math.round(lastPx)) return;
      lastPx = px;
      pts.push(`${px.toFixed(2)},${yHaul(cum).toFixed(2)}`);
    };
    push(json.bins[0].staStartInternalM, 0, true);
    for (let i = 0; i < json.bins.length; i++) {
      push(json.bins[i].staEndInternalM, json.bins[i].cumulativeNetM3, i === json.bins.length - 1);
    }
    return `M${pts.join('L')}`;
  }, [json.bins, xScale, yHaul]);

  const balance = useMemo(() => balancePoints(json.bins).filter((s) => s >= lo && s <= hi), [json.bins, lo, hi]);
  /** Marker text for each balance point, or `null` where a label would
   *  collide with the last one kept (spec §4b). */
  const balanceLabels = useMemo(() => {
    // 2 dp, as every station readout (round-2 decision 4); the label is now
    // centred over its marker, so two labels need their own WIDTH apart, not
    // the old fixed 64 px.
    const text = balance.map((s) =>
      t('stationVolumes.chart.balanceAt', {
        station: staOnly(internalToDisplayRaw(s, json.alignment.staStart, json.alignment.equations), 2),
      }),
    );
    const widest = text.reduce((w, l) => Math.max(w, approxLabelPx(l)), 0);
    return thinLabels(
      balance.map((s) => xScale(s)),
      Math.max(LABEL_MIN_GAP_PX, widest + 8),
    ).map((keep, i) => (keep ? text[i] : null));
  }, [balance, xScale, t, json.alignment.staStart, json.alignment.equations]);
  const equations = useMemo(() => equationMarkers(json), [json]);
  /** Equation tags read `1+020 → 1+500`; thin them on the same collision rule
   *  the balance labels use, or a run of equations overprints itself. */
  const equationLabelled = useMemo(
    () =>
      thinLabels(
        equations.map((e) => xScale(e.station)),
        EQUATION_LABEL_MIN_GAP_PX,
      ),
    [equations, xScale],
  );

  /** Direct labels go on the extreme cut bar and the extreme fill bar only. */
  const extremes = useMemo(() => {
    let cut = -1;
    let fill = -1;
    for (let i = 0; i < bars.length; i++) {
      if (bars[i].cutVolumeM3 > 0 && (cut < 0 || bars[i].cutVolumeM3 > bars[cut].cutVolumeM3)) cut = i;
      if (bars[i].fillVolumeM3 > 0 && (fill < 0 || bars[i].fillVolumeM3 > bars[fill].fillVolumeM3)) fill = i;
    }
    return { cut, fill };
  }, [bars]);


  /** Fixed three-decimal m³ precision for every detailed readout in this view.
   * Pool the original bins, not the drawn super-bins, so resize grouping never
   * changes which data the formatter owns. */
  const fmt = useMemo(() => m3Formatter(json.bins, STATION_VOLUME_DISPLAY_DECIMALS), [json.bins]);

  // ------------------------------------------------------------ live state
  const sel = selection?.layerId === layerId ? selection : null;
  const selLo = sel ? clamp(Math.min(sel.fromBin, sel.toBin), 0, nBins - 1) : -1;
  const selHi = sel ? clamp(Math.max(sel.fromBin, sel.toBin), 0, nBins - 1) : -1;
  const selX1 = sel && nBins ? xScale(json.bins[selLo].staStartInternalM) : 0;
  const selX2 = sel && nBins ? xScale(json.bins[selHi].staEndInternalM) : 0;

  const walkStation = walk?.key === json.alignment.key ? walk.station : null;
  const cursorBin = walkStation == null ? null : binIndexForStation(json.params, nBins, walkStation);
  const hoverGroup =
    hoverBin == null || bars.length === 0
      ? null
      : Math.min(bars.length - 1, Math.floor(hoverBin / Math.max(1, groupSize)));

  const barW = barWidthFor(innerWidth, bars.length);

  /** The plot box is both what we measure and what the tooltip is positioned
   *  against — anchoring the tooltip to the outer wrapper would land it over
   *  the legend row. */
  const setPlotRef = useCallback(
    (el: HTMLDivElement | null) => {
      plotRef.current = el;
      containerRef(el);
    },
    [containerRef],
  );

  /** Internal → display station. Equations make this more than an offset. */
  const displayOf = useCallback(
    (internal: number) => internalToDisplayRaw(internal, json.alignment.staStart, json.alignment.equations),
    [json.alignment.staStart, json.alignment.equations],
  );

  // ------------------------------------------------------------ interaction
  const binAtLocalX = useCallback(
    (x: number) => binIndexForStation(json.params, nBins, xScale.invert(x)),
    [json.params, nBins, xScale],
  );

  const walkToBin = useCallback(
    (i: number) => {
      const b = json.bins[i];
      if (!b) return;
      const station = binCentreStation(b);
      // `follow` is left untouched: the camera moves only if the user asked
      // it to on the walk toolbar.
      setWalk((w) => (w && w.key === json.alignment.key ? { ...w, station } : w));
    },
    [json.bins, json.alignment.key, setWalk],
  );

  /** True inside either brush edge's ±12 px grab zone. */
  const nearHandle = (x: number) =>
    !!sel && (Math.abs(x - selX1) <= HANDLE_GRAB_PX || Math.abs(x - selX2) <= HANDLE_GRAB_PX);

  const onOverlayMove = (e: PointerEvent<SVGRectElement>) => {
    const x = e.clientX - e.currentTarget.getBoundingClientRect().left;
    // The cursor tracks the pointer, not the bin: you can cross into a grab
    // zone without changing bin.
    if (!draggingRef.current) setCursor(nearHandle(x) ? 'ew-resize' : 'crosshair');
    const i = binAtLocalX(x);
    if (i !== lastTipRef.current) {
      lastTipRef.current = i;
      setHover(i);
      const group = Math.min(bars.length - 1, Math.floor(i / Math.max(1, groupSize)));
      const b = bars[group];
      if (b) {
        // Coordinates are relative to the plot box (`setPlotRef`), so the
        // tooltip opens over the chart and not over the legend above it.
        showTooltip({
          tooltipData: { group },
          tooltipLeft: margin.left + xScale(binCentreStation(b)),
          tooltipTop: margin.top + 4,
        });
      }
    }
  };

  const onOverlayLeave = () => {
    lastTipRef.current = null;
    if (!draggingRef.current) setCursor('crosshair');
    setHover(null);
    hideTooltip();
  };

  /**
   * Brush + click-to-walk. `pointermove`/`pointerup` go on the window (the
   * dock's resize grip does the same) so a drag that leaves the plot keeps
   * tracking instead of freezing halfway.
   */
  const onOverlayDown = (e: PointerEvent<SVGRectElement>) => {
    if (e.button !== 0 || nBins === 0) return;
    const originLeft = e.currentTarget.getBoundingClientRect().left;
    const downX = e.clientX - originLeft;
    const downBin = binAtLocalX(downX);

    // Grabbing an existing brush edge re-drags that edge; the opposite edge
    // becomes the anchor.
    let anchor = downBin;
    let handleDrag = false;
    if (sel) {
      if (Math.abs(downX - selX1) <= HANDLE_GRAB_PX) {
        anchor = selHi;
        handleDrag = true;
      } else if (Math.abs(downX - selX2) <= HANDLE_GRAB_PX) {
        anchor = selLo;
        handleDrag = true;
      }
    }

    let moved = handleDrag;
    draggingRef.current = true;
    setCursor('grabbing');

    // One AbortController per drag removes all three listeners at once, and
    // the unmount effect above can abort the same one.
    dragAbortRef.current?.abort();
    const ac = new AbortController();
    dragAbortRef.current = ac;

    const move = (ev: globalThis.PointerEvent) => {
      const i = binAtLocalX(ev.clientX - originLeft);
      if (i !== downBin) moved = true;
      if (moved) setSelection({ layerId, fromBin: anchor, toBin: i });
    };
    const up = (ev: globalThis.PointerEvent) => {
      ac.abort();
      if (dragAbortRef.current === ac) dragAbortRef.current = null;
      draggingRef.current = false;
      setCursor(nearHandle(ev.clientX - originLeft) ? 'ew-resize' : 'crosshair');
      if (moved) return;
      // A click, not a drag: clear the brush and walk to that bin.
      setSelection(null);
      walkToBin(binAtLocalX(ev.clientX - originLeft));
    };
    window.addEventListener('pointermove', move, { signal: ac.signal });
    window.addEventListener('pointerup', up, { signal: ac.signal });
    window.addEventListener('pointercancel', up, { signal: ac.signal });
  };

  /**
   * One tab stop, arrow-driven (spec §4b). `←/→` step the walked station one
   * bin and `Shift` makes that ten — unless a brush exists, in which case
   * `Shift+←/→` moves its trailing edge, which is the only way to adjust a
   * brush without a pointer.
   */
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (nBins === 0) return;
    if (e.key === 'Escape') {
      if (!sel) return; // nothing of ours to clear — let the dock close
      e.preventDefault();
      e.stopPropagation();
      setSelection(null);
      return;
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      e.stopPropagation();
      walkToBin(e.key === 'Home' ? 0 : nBins - 1);
      return;
    }
    const dir = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
    if (dir === 0) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.shiftKey && sel) {
      setSelection({ layerId, fromBin: sel.fromBin, toBin: clamp(sel.toBin + dir, 0, nBins - 1) });
      return;
    }
    // No walk yet: → lands on the first bin and ← on the last, instead of
    // `(cursorBin ?? 0) + 1` skipping bin 0 (clearance review #16).
    if (cursorBin == null) {
      walkToBin(dir > 0 ? 0 : nBins - 1);
      return;
    }
    walkToBin(clamp(cursorBin + dir * (e.shiftKey ? 10 : 1), 0, nBins - 1));
  };

  // ------------------------------------------------------------ copy
  const [sumCut, sumFill, sumNet] = formatM3Row(
    [json.summary.cutVolumeM3, json.summary.fillVolumeM3, json.summary.netVolumeM3],
    STATION_VOLUME_DISPLAY_DECIMALS,
  );
  const rangeFrom = nBins ? staOnly(json.bins[0].staStartDisplayM) : '';
  const rangeTo = nBins ? staOnly(json.bins[nBins - 1].staEndDisplayM) : '';
  const ariaSummary = t('stationVolumes.chart.ariaSummary', {
    bins: nBins,
    from: rangeFrom,
    to: rangeTo,
    cut: sumCut,
    fill: sumFill,
    net: sumNet,
  });
  const cursorLabel =
    cursorBin != null && json.bins[cursorBin]
      ? staOnly(json.bins[cursorBin].staStartDisplayM)
      : t('stationVolumes.chart.noCursor');

  const tipBin = tooltipData ? bars[tooltipData.group] : null;
  /**
   * The station LINE the hovered interval starts at — NOT `stationLines[
   * tooltipData.group]`, which is only the same index while the chart is
   * drawing one bar per bin. Above ~1 bar per 3 px the bars are super-bins and
   * the two indices diverge; the interval's own start station is the honest
   * key, and it is always a station line.
   */
  const tipStationLine =
    tipBin && stationLines
      ? (stationLines.reduce<(typeof stationLines)[number] | null>(
          (best, s) =>
            best == null ||
            Math.abs(s.staInternalM - tipBin.staStartInternalM) < Math.abs(best.staInternalM - tipBin.staStartInternalM)
              ? s
              : best,
          null,
        ) ?? null)
      : null;
  const tipRows = tipBin
    ? (() => {
        const rows: { label: string; value: string; ink?: string }[] = [
          { label: t('stationVolumes.panel.cut'), value: `${fmt.volume(tipBin.cutVolumeM3)} m³` },
          { label: t('stationVolumes.panel.fill'), value: `${fmt.volume(tipBin.fillVolumeM3)} m³` },
          { label: t('stationVolumes.panel.net'), value: `${fmt.volume(tipBin.netVolumeM3)} m³` },
          { label: t('stationVolumes.chart.cumulative'), value: `${fmt.cumulative(tipBin.cumulativeNetM3)} m³` },
          {
            label: t('stationVolumes.chart.endArea'),
            value: `${formatM2(tipBin.cutAreaM2)} / ${formatM2(tipBin.fillAreaM2)} m²`,
          },
          {
            label: t('stationVolumes.chart.coverage'),
            value: tipBin.lowCoverage
              ? t('stationVolumes.chart.coverageLow', { pct: formatPct(tipBin.coveragePct) })
              : `${formatPct(tipBin.coveragePct)} %`,
          },
        ];
        // Crossfall is a per-station-LINE figure, so it is only in the
        // tooltip while its own pane is showing — the row would otherwise
        // claim a bin quantity it is not. The line quoted is the one at the
        // hovered interval's upstream end (`tipStationLine`).
        //
        // The cell is the TABLE's cell, in miniature: the figure, then the
        // verdict's glyph. NOT gapped on nullness the way the pane's as-built
        // line is — `scripts/crossfall.py` nulls its figures only on the
        // COVERAGE path, so an UNCERTAIN station has a real `asbuiltPct` and
        // hiding it here while the table printed it (and the pane gapped at
        // it) gave one station three different accounts of itself. The em
        // dash is for the sides that genuinely have no figure; the glyph is
        // what says the figure has no standing. `formatSigned`, so the minus
        // is the house U+2212 and a positive crossfall carries its `+` —
        // character for character what the table's cell prints.
        if (crossfallOn && tipStationLine?.crossfall) {
          const cell = (b: CrossfallSide | null | undefined) => {
            if (!b) return '—';
            const figure = b.asbuiltPct == null ? '—' : `${formatSigned(b.asbuiltPct, 2)} %`;
            const glyph = (SEVERITY[b.verdict] ?? SEVERITY.INCONCLUSIVE).glyph;
            return `${figure} ${glyph}`;
          };
          rows.push({
            label: `${t('stationVolumes.chart.lowerPaneCrossfall')} ${t('stationVolumes.chart.legendCrossfallLeft')}`,
            value: cell(tipStationLine.crossfall.left),
            ink: 'var(--sv-xf-left)',
          });
          rows.push({
            label: `${t('stationVolumes.chart.lowerPaneCrossfall')} ${t('stationVolumes.chart.legendCrossfallRight')}`,
            value: cell(tipStationLine.crossfall.right),
            ink: 'var(--sv-xf-right)',
          });
        }
        // An equation inside the hovered bin is the one thing that makes the
        // display stations either side of this bar discontinuous. It has to be
        // readable, not hidden in an SVG <title> under the hit overlay.
        const last = tipBin.toBin === nBins - 1;
        for (const e of equations) {
          const inBin =
            e.station >= tipBin.staStartInternalM &&
            (e.station < tipBin.staEndInternalM || (last && e.station <= tipBin.staEndInternalM));
          if (inBin) {
            rows.push({
              label: t('stationVolumes.chart.equationRow'),
              value: t('stationVolumes.chart.equationTag', {
                from: staOnly(e.from),
                to: staOnly(e.to),
              }),
            });
          }
        }
        return rows;
      })()
    : [];

  const tableHeaders = [
    t('stationVolumes.chart.colStation'),
    t('stationVolumes.panel.cut'),
    t('stationVolumes.panel.fill'),
    t('stationVolumes.panel.net'),
    t('stationVolumes.chart.cumulative'),
    t('stationVolumes.chart.coverage'),
  ];
  const tableRows = bars.map((b) => ({
    key: b.fromBin,
    cells: [
      `${staOnly(b.staStartDisplayM)} – ${staOnly(b.staEndDisplayM)}`,
      fmt.volume(b.cutVolumeM3),
      fmt.volume(b.fillVolumeM3),
      fmt.volume(b.netVolumeM3),
      fmt.cumulative(b.cumulativeNetM3),
      b.lowCoverage
        ? t('stationVolumes.chart.coverageLow', { pct: formatPct(b.coveragePct) })
        : `${formatPct(b.coveragePct)} %`,
    ],
  }));

  const currentRow =
    cursorBin == null ? null : (bars.find((b) => cursorBin >= b.fromBin && cursorBin <= b.toBin)?.fromBin ?? null);

  return (
    <div className='flex h-full min-h-0 w-full flex-col gap-1.5 text-slate-700 dark:text-stone-300'>
      <style>{CHART_CSS}</style>

      {/* The legend row sits OUTSIDE the chart region on purpose: the chart is
          one tab stop (spec §4b), and a button nested inside it would be a
          second one that no keyboard user could reach past the arrow keys. */}
      <ChartLegend
        fillLabel={t('stationVolumes.chart.legendFill')}
        cutLabel={t('stationVolumes.chart.legendCut')}
        cutConvention={{ full: t(CUT_LEGEND_KEY) }}
        fillConvention={{ full: t(FILL_LEGEND_KEY) }}
        coverageScale={{
          label: t('stationVolumes.chart.legendCoverage'),
          help: t(
            json.params.coverageBasis === 'REFERENCE_SURFACE'
              ? kind === 'deviation'
                ? 'stationVolumes.chart.legendCoverageHelpRefDeviation'
                : 'stationVolumes.chart.legendCoverageHelpRef'
              : kind === 'deviation'
                ? 'stationVolumes.chart.legendCoverageHelpDeviation'
                : 'stationVolumes.chart.legendCoverageHelp',
          ),
          bands: COVERAGE_BANDS.map((b, i) => ({
            color: b.color,
            label: i < COVERAGE_BANDS.length - 1 ? `${b.minPct}–${COVERAGE_BANDS[i + 1].minPct}` : `≥${b.minPct}`,
          })),
        }}
        groupedLabel={
          groupSize > 1
            ? t('stationVolumes.chart.grouped', {
                interval: json.params.stationIntervalM,
                k: groupSize,
                grouped: json.params.stationIntervalM * groupSize,
              })
            : null
        }
        groupedHelp={t('stationVolumes.chart.groupedHelp')}
        hint={t('stationVolumes.chart.hint')}
        crossfallLegend={
          showCrossfall
            ? {
                left: t('stationVolumes.chart.legendCrossfallLeft'),
                right: t('stationVolumes.chart.legendCrossfallRight'),
                design: t('stationVolumes.chart.legendCrossfallDesign'),
                tolerance: t('stationVolumes.chart.legendCrossfallTolerance'),
              }
            : null
        }
        lowerPane={
          (canShowEndArea || canShowCrossfall) && showLower
            ? {
                value: lowerPane,
                onChange: setLowerPane,
                haulLabel: t('stationVolumes.chart.lowerPaneHaul'),
                endAreaLabel: t('stationVolumes.chart.lowerPaneEndArea'),
                haulHelp: t('stationVolumes.chart.lowerPaneHaulHelp'),
                endAreaHelp: t('stationVolumes.chart.endAreaSeriesHelp'),
                ...(canShowCrossfall
                  ? {
                      crossfallLabel: t('stationVolumes.chart.lowerPaneCrossfall'),
                      crossfallHelp: t('stationVolumes.chart.crossfallSeriesHelp', { tol: crossfallTol }),
                    }
                  : {}),
                groupLabel: t('stationVolumes.chart.lowerPaneGroup'),
                help: t('stationVolumes.chart.lowerPaneHelp'),
              }
            : null
        }
        massHaul={
          compact
            ? {
                label: crossfallOn
                  ? t('stationVolumes.chart.lowerPaneCrossfall')
                  : endAreaOn
                    ? t('stationVolumes.chart.lowerPaneEndArea')
                    : t('stationVolumes.chart.massHaulToggle'),
                help:
                  crossfallOn || endAreaOn
                    ? t('stationVolumes.chart.lowerPaneHelp')
                    : t('stationVolumes.chart.massHaulToggleHelp'),
                on: massHaul,
                onToggle: () => setMassHaul((v) => !v),
              }
            : null
        }
      />

      {/* ---- Chart region: the single tab stop, arrow-driven ---- */}
      <div
        data-testid='sv-chart'
        className='sv-chart flex min-h-0 w-full flex-1 flex-col outline-none'
        role='application'
        tabIndex={0}
        aria-label={t('stationVolumes.chart.ariaCursor', { station: cursorLabel })}
        onKeyDown={onKeyDown}
      >
        <div ref={setPlotRef} className='relative min-h-0 w-full flex-1'>
          <svg width={plotWidth} height={plotHeight} role='img' aria-label={ariaSummary} data-testid='sv-chart-svg'>
            <defs>
              {/* 45° for cut, 135° for fill — a polarity that survives forced
                colours and greyscale print. */}
              <pattern
                id='sv-hatch-cut'
                width={6}
                height={6}
                patternTransform='rotate(45)'
                patternUnits='userSpaceOnUse'
              >
                <line x1={0} y1={0} x2={0} y2={6} stroke='currentColor' strokeWidth={3} />
              </pattern>
              <pattern
                id='sv-hatch-fill'
                width={6}
                height={6}
                patternTransform='rotate(135)'
                patternUnits='userSpaceOnUse'
              >
                <line x1={0} y1={0} x2={0} y2={6} stroke='currentColor' strokeWidth={3} />
              </pattern>
            </defs>

            <Group left={margin.left} top={margin.top}>
              {/* Gridlines go DOWN FIRST, under both panes — drawn after them
                they notch through every bar (house style:
                `ElevationProfilePanel.tsx:598-620`). */}
              {xScale.ticks(Math.max(2, Math.floor(innerWidth / 90))).map((tick) => (
                <line
                  key={`gx-${tick}`}
                  x1={xScale(tick)}
                  x2={xScale(tick)}
                  y1={0}
                  y2={axisY}
                  stroke='var(--sv-grid)'
                  pointerEvents='none'
                />
              ))}

              <BarPane
                bars={bars}
                show={showBars}
                xScale={xScale}
                yScale={yBar}
                barW={barW}
                paneHeight={barPlotH}
                stripY={barPlotH + COVERAGE_STRIP_GAP_PX}
                stripH={COVERAGE_STRIP_PX}
                coverageTitle={(pct) => t('stationVolumes.chart.coverageStripTitle', { pct: formatPct(pct) })}
                innerWidth={innerWidth}
                marginLeft={margin.left}
                selLo={sel ? selLo : -1}
                selHi={sel ? selHi : -1}
                hoverGroup={hoverGroup}
                extremes={extremes}
                axisLabel={t('stationVolumes.chart.volumeAxis')}
                formatVolume={fmt.volume}
              />

              <EndAreaPane
                show={showEndArea}
                top={haulPaneTop}
                cutPath={endAreaSeries.cut}
                fillPath={endAreaSeries.fill}
                yScale={yEndArea}
                innerWidth={innerWidth}
                marginLeft={margin.left}
                axisLabel={t('stationVolumes.chart.endAreaAxis')}
                axisHelp={t('stationVolumes.chart.endAreaSeriesHelp')}
              />

              <CrossfallPane
                show={showCrossfall}
                top={haulPaneTop}
                paths={crossfallSeries}
                yScale={yCrossfall}
                innerWidth={innerWidth}
                marginLeft={margin.left}
                axisLabel={t('stationVolumes.chart.crossfallAxis')}
                axisHelp={t('stationVolumes.chart.crossfallSeriesHelp', { tol: crossfallTol })}
                leftLabel={t('stationVolumes.chart.legendCrossfallLeft')}
                rightLabel={t('stationVolumes.chart.legendCrossfallRight')}
                designLabel={t('stationVolumes.chart.legendCrossfallDesign')}
                toleranceLabel={t('stationVolumes.chart.legendCrossfallTolerance')}
              />

              <HaulPane
                show={showHaul}
                top={haulPaneTop}
                path={haulPath}
                xScale={xScale}
                yScale={yHaul}
                innerWidth={innerWidth}
                marginLeft={margin.left}
                balance={balance}
                balanceLabels={balanceLabels}
                axisLabel={t('stationVolumes.chart.cumulativeAxis')}
              />

              {/* ------------------------------------------------ shared station axis */}
              <AxisBottom
                scale={xScale}
                top={axisY}
                numTicks={Math.max(2, Math.floor(innerWidth / 90))}
                stroke='currentColor'
                tickStroke='currentColor'
                // Whole metres, as on the clearance chart: a tick names a
                // place on the axis, not a measurement — the tooltip and
                // the range readout keep the full precision.
                tickFormat={(v) => staOnly(displayOf(Number(v)), 0)}
                // The last label anchors at its end rather than run past the
                // plot box ("13+20(" — audit §A4).
                tickLabelProps={stationTickLabelProps(
                  xScale,
                  (v) => staOnly(displayOf(v), 0),
                  innerWidth + margin.right,
                  { className: 'sv-sta' },
                )}
              />

              {/* Station equations: the x-axis is in display stations, so the
                jump has to be marked or the axis silently lies. */}
              {equations.map((e, i) => (
                <g key={`eq-${e.station}`} data-testid='sv-equation'>
                  <line
                    x1={xScale(e.station)}
                    x2={xScale(e.station)}
                    y1={0}
                    y2={axisY}
                    stroke='currentColor'
                    strokeOpacity={0.45}
                    strokeDasharray='3,3'
                    pointerEvents='none'
                  />
                  <polygon
                    points={`${xScale(e.station)},${axisY - 5} ${xScale(e.station) - 4},${axisY + 2} ${xScale(e.station) + 4},${axisY + 2}`}
                    fill='currentColor'
                  />
                  {equationLabelled[i] && (
                    <text
                      x={xScale(e.station) + 6}
                      y={9}
                      fontSize={TICK_FONT}
                      className='sv-sta'
                      fill='currentColor'
                      opacity={0.85}
                    >
                      {t('stationVolumes.chart.equationTag', { from: staOnly(e.from), to: staOnly(e.to) })}
                    </text>
                  )}
                  <title>{t('stationVolumes.chart.equation', { from: staOnly(e.from), to: staOnly(e.to) })}</title>
                </g>
              ))}

              {/* ------------------------------------------------ brush */}
              {sel && (
                <g data-testid='sv-brush' pointerEvents='none'>
                  <rect
                    x={Math.min(selX1, selX2)}
                    width={Math.max(2, Math.abs(selX2 - selX1))}
                    y={0}
                    height={axisY}
                    fill='currentColor'
                    fillOpacity={0.07}
                  />
                  {[selX1, selX2].map((x, i) => {
                    const gripY = Math.max(0, (axisY - HANDLE_GRIP_H) / 2);
                    return (
                      <g key={`h-${i}`}>
                        <line
                          className='sv-handle'
                          x1={x}
                          x2={x}
                          y1={0}
                          y2={axisY}
                          stroke='var(--sv-haul)'
                          strokeOpacity={0.7}
                          strokeWidth={2}
                        />
                        {/* The grip: 28 px tall so it is findable, ringed in the
                          surface colour so it reads as a control and not as
                          data. Its hit area is the ±12 px zone on the overlay. */}
                        <rect
                          className='sv-handle'
                          data-testid='sv-brush-handle'
                          x={x - HANDLE_GRIP_W / 2}
                          y={gripY}
                          width={HANDLE_GRIP_W}
                          height={Math.min(HANDLE_GRIP_H, Math.max(1, axisY))}
                          rx={HANDLE_GRIP_W / 2}
                          fill='var(--sv-haul)'
                          stroke='var(--sv-surface)'
                          strokeWidth={2}
                        />
                        <line
                          x1={x - 1.5}
                          x2={x - 1.5}
                          y1={gripY + 9}
                          y2={gripY + HANDLE_GRIP_H - 9}
                          stroke='var(--sv-surface)'
                        />
                        <line
                          x1={x + 1.5}
                          x2={x + 1.5}
                          y1={gripY + 9}
                          y2={gripY + HANDLE_GRIP_H - 9}
                          stroke='var(--sv-surface)'
                        />
                      </g>
                    );
                  })}
                </g>
              )}

              {/* ------------------------------------------------ cursors */}
              {hoverBin != null && json.bins[hoverBin] && (
                <line
                  className='sv-hover-cursor'
                  x1={xScale(binCentreStation(json.bins[hoverBin]))}
                  x2={xScale(binCentreStation(json.bins[hoverBin]))}
                  y1={0}
                  y2={axisY}
                  stroke='currentColor'
                  strokeOpacity={0.4}
                  strokeDasharray='2,2'
                />
              )}
              {walkStation != null && walkStation >= lo && walkStation <= hi && (
                <g
                  className='sv-walk-cursor'
                  data-testid='sv-walk-cursor'
                  style={{ transform: `translateX(${xScale(walkStation)}px)` }}
                >
                  <line x1={0} x2={0} y1={0} y2={axisY} stroke='var(--sv-walk)' strokeWidth={1.5} />
                  <polygon points={`0,${axisY} -4,${axisY + 6} 4,${axisY + 6}`} fill='var(--sv-walk)' />
                </g>
              )}

              {/* Interaction overlay: hover, click-to-walk, drag-to-brush. It
                spans both panes, so every mark has a ≥ 24 px hit target. */}
              <Bar
                data-testid='sv-overlay'
                x={0}
                y={0}
                width={innerWidth}
                height={Math.max(1, axisY)}
                fill='transparent'
                // `touch-action: none`: on touch the browser would claim the
                // drag (pointercancel) and the brush / readout would stop.
                style={{ cursor, touchAction: 'none' }}
                onPointerDown={onOverlayDown}
                onPointerMove={onOverlayMove}
                onPointerLeave={onOverlayLeave}
              />
            </Group>
          </svg>

          {tooltipOpen && tipBin && (
            <TooltipInPortal
              top={tooltipTop}
              left={tooltipLeft}
              style={FLOATING_TOOLTIP_STYLE}
              className='pointer-events-none !rounded-md !bg-slate-800 !px-2 !py-1.5 !text-xs !text-white !shadow-lg'
            >
              {/* The `STA` tag, exactly as the 3D tooltip carries it (spec
                  §4b): a bare `1+100.00 – 1+530.00` floating over a chart is
                  the one place a reader has no column header to tell them
                  what the number is. */}
              <TooltipCard
                station={`${staTag(tipBin.staStartDisplayM)} – ${staOnly(tipBin.staEndDisplayM)}`}
                rows={tipRows}
              />
            </TooltipInPortal>
          )}
        </div>
      </div>

      {/* OUTSIDE the `role="application"` region, deliberately. An application
          region tells assistive tech to hand every keystroke to the app and to
          stop exposing the descendants as a document, which took the one
          accessible view of these numbers off the table — literally: the rows
          were unreachable by table navigation. As a sibling the chart keeps its
          arrow-key model and the data keeps its `<table>` semantics. */}
      <HiddenDataTable
        caption={
          groupSize > 1
            ? t('stationVolumes.chart.srCaptionGrouped', { k: groupSize })
            : t('stationVolumes.chart.srCaption')
        }
        headers={tableHeaders}
        rows={tableRows}
        currentRow={currentRow}
      />
    </div>
  );
};
